/**
 * Black Friday Countdown Timer
 * Handles countdown logic, state management, and animations
 */

class BlackFridayCountdown {
  constructor() {
    this.wrapper = document.querySelector('.black-friday-countdown-wrapper');
    if (!this.wrapper) return;

    this.targetDate = new Date(this.wrapper.dataset.targetDate);
    this.endDate = new Date(this.wrapper.dataset.endDate);
    
    this.elements = {
      days: document.getElementById('bf-days'),
      hours: document.getElementById('bf-hours'),
      minutes: document.getElementById('bf-minutes'),
      seconds: document.getElementById('bf-seconds'),
      messageText: document.getElementById('bf-message-text'),
      countdownTimer: document.getElementById('bf-countdown-timer'),
      statusMessage: document.getElementById('bf-status-message'),
      endedState: document.getElementById('bf-ended-state')
    };

    this.init();
  }

  init() {
    // Initial check
    this.updateCountdown();
    
    // Update every second
    this.intervalId = setInterval(() => {
      this.updateCountdown();
    }, 1000);

    // Add animation on numbers change
    this.setupNumberChangeAnimation();
  }

  updateCountdown() {
    const now = new Date().getTime();
    const targetTime = this.targetDate.getTime();
    const endTime = this.endDate.getTime();

    // Check if sale has ended
    if (now > endTime) {
      this.showEndedState();
      return;
    }

    // Check if sale is active
    if (now >= targetTime && now <= endTime) {
      this.showActiveState();
      return;
    }

    // Sale hasn't started yet - countdown to start
    const distance = targetTime - now;
    this.renderCountdown(distance);
  }

  renderCountdown(distance) {
    if (distance < 0) {
      this.showActiveState();
      return;
    }

    // Calculate time units
    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((distance % (1000 * 60)) / 1000);

    // Update DOM with animation
    this.updateNumber(this.elements.days, days);
    this.updateNumber(this.elements.hours, hours);
    this.updateNumber(this.elements.minutes, minutes);
    this.updateNumber(this.elements.seconds, seconds);
  }

  updateNumber(element, value) {
    if (!element) return;
    
    const formattedValue = this.padZero(value);
    const currentValue = element.textContent;

    if (currentValue !== formattedValue) {
      // Trigger animation
      element.style.animation = 'none';
      setTimeout(() => {
        element.textContent = formattedValue;
        element.style.animation = 'numberFlip 0.6s ease';
      }, 10);
    }
  }

  padZero(num) {
    return num.toString().padStart(2, '0');
  }

  showActiveState() {
    const now = new Date().getTime();
    const endTime = this.endDate.getTime();
    const remainingTime = endTime - now;

    if (remainingTime <= 0) {
      this.showEndedState();
      return;
    }

    // Update message to show sale is active
    if (this.elements.messageText) {
      this.elements.messageText.textContent = '🔥 Black Friday ist jetzt aktiv! Schnapp dir die besten Deals!';
      this.elements.statusMessage.style.background = 'transparent';
      this.elements.statusMessage.style.padding = '0';
      this.elements.statusMessage.style.borderRadius = '0';
      this.elements.statusMessage.style.border = 'none';
      this.elements.statusMessage.style.animation = 'none';
    }

    // Continue countdown to end
    this.renderCountdown(remainingTime);
  }

  showEndedState() {
    // Clear interval
    clearInterval(this.intervalId);

    // Hide countdown
    if (this.elements.countdownTimer) {
      this.elements.countdownTimer.style.display = 'none';
    }
    if (this.elements.statusMessage) {
      this.elements.statusMessage.style.display = 'none';
    }

    // Show ended state
    if (this.elements.endedState) {
      this.elements.endedState.style.display = 'block';
      this.elements.endedState.style.animation = 'fadeInUp 0.8s ease-out';
    }

    // Hide main countdown container
    const countdownContainer = document.querySelector('.bf-countdown-container');
    if (countdownContainer) {
      countdownContainer.style.display = 'none';
    }
  }

  setupNumberChangeAnimation() {
    // Add CSS for number flip animation if not already present
    if (!document.getElementById('bf-countdown-animation-styles')) {
      const style = document.createElement('style');
      style.id = 'bf-countdown-animation-styles';
      style.textContent = `
        @keyframes numberFlip {
          0% {
            transform: rotateX(0deg);
            opacity: 1;
          }
          50% {
            transform: rotateX(90deg);
            opacity: 0.5;
          }
          100% {
            transform: rotateX(0deg);
            opacity: 1;
          }
        }
      `;
      document.head.appendChild(style);
    }
  }

  destroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }
}

// Enhanced Features
class CountdownEnhancements {
  constructor() {
    this.initUrgencyEffects();
    this.initParticleInteraction();
  }

  initUrgencyEffects() {
    // Add urgency effects when time is running low
    setInterval(() => {
      const daysElement = document.getElementById('bf-days');
      if (!daysElement) return;

      const days = parseInt(daysElement.textContent);
      const hoursElement = document.getElementById('bf-hours');
      const hours = hoursElement ? parseInt(hoursElement.textContent) : 0;

      // Last day effect
      if (days === 0 && hours < 24) {
        document.querySelectorAll('.bf-countdown-card').forEach(card => {
          card.style.borderColor = '#E1090C';
          card.style.animation = 'urgentPulse 1s infinite';
        });

        // Add urgent style if not exists
        if (!document.getElementById('urgent-animation-style')) {
          const style = document.createElement('style');
          style.id = 'urgent-animation-style';
          style.textContent = `
            @keyframes urgentPulse {
              0%, 100% {
                box-shadow: 0 8px 32px rgba(225, 9, 12, 0.3);
                transform: scale(1);
              }
              50% {
                box-shadow: 0 12px 40px rgba(225, 9, 12, 0.6);
                transform: scale(1.02);
              }
            }
          `;
          document.head.appendChild(style);
        }
      }
    }, 1000);
  }

  initParticleInteraction() {
    // Add mouse interaction to particles
    const wrapper = document.querySelector('.black-friday-countdown-wrapper');
    if (!wrapper) return;

    wrapper.addEventListener('mousemove', (e) => {
      const particles = document.querySelectorAll('.bf-particle');
      const rect = wrapper.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      particles.forEach((particle, index) => {
        const particleRect = particle.getBoundingClientRect();
        const particleX = particleRect.left - rect.left + particleRect.width / 2;
        const particleY = particleRect.top - rect.top + particleRect.height / 2;

        const distance = Math.sqrt(
          Math.pow(x - particleX, 2) + Math.pow(y - particleY, 2)
        );

        if (distance < 100) {
          const angle = Math.atan2(y - particleY, x - particleX);
          const moveDistance = (100 - distance) / 5;
          
          particle.style.transform = `translate(${-Math.cos(angle) * moveDistance}px, ${-Math.sin(angle) * moveDistance}px)`;
        } else {
          particle.style.transform = 'translate(0, 0)';
        }
      });
    });
  }
}

// Social Proof & Urgency Messages
class CountdownMessages {
  constructor() {
    // Nachrichten VOR dem Event
    this.messagesBeforeEvent = [
      '⏰ Bald geht es los - Sei bereit für die besten Deals!',
      '🔔 Nur noch kurze Zeit bis zum Start!',
      '💎 Die besten Angebote des Jahres erwarten dich!',
      '🚀 Bereite deine Wunschliste vor - Es kann bald losgehen!',
      '⭐ Exklusive Deals starten in Kürze!',
      '🎁 Verpasse nicht den Start unserer Black Friday Aktion!'
    ];
    
    // Nachrichten WÄHREND des Events
    this.messagesDuringEvent = [
      '⚡ Über 1.000 Kunden haben heute bereits bestellt!',
      '🔥 Nur noch wenige Artikel auf Lager!',
      '💎 Die beliebtesten Produkte sind fast ausverkauft!',
      '⏰ Sichere dir jetzt die besten Deals!',
      '🎯 Beste Preise des Jahres - garantiert!',
      '🚀 Schneller Versand noch vor Weihnachten!',
      '💝 Perfekte Geschenke zu unschlagbaren Preisen!',
      '👥 Gerade eben haben 5 Kunden dieses Produkt gekauft!',
      '⚡ Die Lagerbestände schwinden - Jetzt zugreifen!'
    ];
    
    this.currentIndex = 0;
    this.currentMessages = this.messagesBeforeEvent;
    this.initRotatingMessages();
  }

  isEventActive() {
    const wrapper = document.querySelector('.black-friday-countdown-wrapper');
    if (!wrapper) return false;
    
    const targetDate = new Date(wrapper.dataset.targetDate);
    const endDate = new Date(wrapper.dataset.endDate);
    const now = new Date();
    
    return now >= targetDate && now <= endDate;
  }

  initRotatingMessages() {
    const messageElement = document.getElementById('bf-message-text');
    if (!messageElement) return;

    // Check event status and update messages accordingly
    const updateMessageList = () => {
      if (this.isEventActive()) {
        this.currentMessages = this.messagesDuringEvent;
      } else {
        this.currentMessages = this.messagesBeforeEvent;
      }
      this.currentIndex = 0; // Reset index when switching
    };

    // Initial check
    updateMessageList();

    // Rotate messages every 5 seconds
    setInterval(() => {
      // Check if event status changed
      updateMessageList();
      
      this.currentIndex = (this.currentIndex + 1) % this.currentMessages.length;
      
      messageElement.style.animation = 'none';
      setTimeout(() => {
        messageElement.textContent = this.currentMessages[this.currentIndex];
        messageElement.style.animation = 'fadeInUp 0.5s ease-out';
      }, 100);
    }, 5000);

    // Also check every minute if event status changed
    setInterval(() => {
      updateMessageList();
    }, 60000);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBlackFridayCountdown);
} else {
  initBlackFridayCountdown();
}

function initBlackFridayCountdown() {
  // Initialize countdown
  const countdown = new BlackFridayCountdown();
  
  // Initialize enhancements
  const enhancements = new CountdownEnhancements();
  
  // Initialize rotating messages
  const messages = new CountdownMessages();

  // Cleanup on page unload
  window.addEventListener('beforeunload', () => {
    if (countdown) {
      countdown.destroy();
    }
  });

  // Add scroll reveal effect
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.style.opacity = '1';
        entry.target.style.transform = 'translateY(0)';
      }
    });
  }, {
    threshold: 0.1
  });

  // Observe countdown elements
  document.querySelectorAll('.bf-countdown-unit, .bf-feature-item').forEach(el => {
    el.style.opacity = '0';
    el.style.transform = 'translateY(30px)';
    el.style.transition = 'all 0.6s ease-out';
    observer.observe(el);
  });
}

// Export for potential external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { BlackFridayCountdown, CountdownEnhancements, CountdownMessages };
}

